import { createFileRoute, Outlet, Link, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import { Activity, Clock, DollarSign, HardHat, FileBarChart } from "lucide-react";
import { cn } from "@/lib/utils";

import { RequirePremium } from "@/components/subscription/RequirePremium";

export const Route = createFileRoute("/_app/reports")({
  component: ReportsRouteComponent,
  head: () => ({
    meta: [
      { title: "Relatórios — Business Dite Ads" },
      {
        name: "description",
        content: "Hub central de relatórios: GPS, horas, financeiro e projetos.",
      },
    ],
  }),
});

function ReportsRouteComponent() {
  return (
    <RequirePremium featureKey="nav.reports">
      <ReportsLayout />
    </RequirePremium>
  );
}

const TABS = [
  { to: "/reports/gps" as const, labelKey: "pages.reports.layout.tabs.gps", icon: Activity },
  { to: "/reports/payroll" as const, labelKey: "pages.reports.layout.tabs.payroll", icon: Clock },
  {
    to: "/reports/financial" as const,
    labelKey: "pages.reports.layout.tabs.financial",
    icon: DollarSign,
  },
  {
    to: "/reports/projects" as const,
    labelKey: "pages.reports.layout.tabs.projects",
    icon: HardHat,
  },
];

function ReportsLayout() {
  const { t } = useTranslation();
  const { hasRole, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !hasRole("admin")) {
      navigate({ to: "/dashboard" });
    }
  }, [hasRole, loading, navigate]);

  // Redirect /reports → /reports/gps
  useEffect(() => {
    if (location.pathname === "/reports" || location.pathname === "/reports/") {
      navigate({ to: "/reports/gps", replace: true });
    }
  }, [location.pathname, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-primary/10 p-2 text-primary">
          <FileBarChart className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("pages.reports.layout.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("pages.reports.layout.subtitle")}</p>
        </div>
      </div>

      <div className="border-b border-border">
        <nav className="-mb-px flex gap-1 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = location.pathname.startsWith(tab.to);
            return (
              <Link
                key={tab.to}
                to={tab.to}
                className={cn(
                  "flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors",
                  active
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:border-border hover:text-foreground",
                )}
              >
                <Icon className="h-4 w-4" />
                {t(tab.labelKey)}
              </Link>
            );
          })}
        </nav>
      </div>

      <Outlet />
    </div>
  );
}
